import { useCallback, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { setSnackbar } from 'shared/store/slices/snackBarSlice';

import { actionMap } from '../components/NewShipment/ActionMap';

/* ------------------------------------------------------------------ *
 * Pure transform helpers (no state, no dispatch)
 * ------------------------------------------------------------------ */

/** Collect only the form field keys from dialogConfig (used to filter parsed_data). */
function getFormFieldKeys(config) {
    const keys = new Set();
    (config.sections || []).forEach((section) => {
        (section.fields || []).forEach((f) => {
            if (f.key) keys.add(f.key);
        });
        (section.sections || []).forEach((sub) => {
            if (sub.key) keys.add(sub.key);
        });
    });
    return keys;
}

/** Form field key → parsed_data key mapping. */
const PARSED_FIELD_MAP = {
    il_location_id_pol: { parsedKey: 'pol', kind: 'location' },
    il_location_id_pod: { parsedKey: 'pod', kind: 'location' },
    il_location_id_fpod: { parsedKey: 'fpod', kind: 'location' },
    il_location_id_aol: { parsedKey: 'aol', kind: 'location' },
    il_location_id_aod: { parsedKey: 'aod', kind: 'location' },
    il_location_id_faod: { parsedKey: 'faod', kind: 'location' },
    il_opus_vessel_id: { parsedKey: 'vessel', kind: 'vessel' },
};

/** True when the parsed value is already a resolved object (has id/name). */
function isResolvedObject(value) {
    return typeof value === 'object' && value !== null && ('id' in value || 'name' in value);
}

/** Convert a mapped field value (location/vessel) into the shape the form expects. */
function resolveMappedValue(parsedVal, kind) {
    const isObject = isResolvedObject(parsedVal);
    if (kind === 'vessel') {
        if (isObject && parsedVal.id == null) return String(parsedVal.name ?? '');
        return isObject ? parsedVal : { id: null, name: String(parsedVal) };
    }
    return isObject ? parsedVal : { id: null, code: String(parsedVal), name: String(parsedVal) };
}

/**
 * Split one applyPayload into a main-form source and an HBL list.
 * Accepts both shapes: an MBL+HBL bundle and a single parsed result.
 */
function splitApplyPayload(applyPayload) {
    const isMblHblPayload = applyPayload?.mblData !== undefined || applyPayload?.hblList !== undefined;
    if (isMblHblPayload) {
        return { mainSource: applyPayload.mblData ?? null, hblList: applyPayload.hblList || [] };
    }
    const singleParsedData = applyPayload?.parsedData ?? applyPayload;
    return {
        mainSource: singleParsedData && typeof singleParsedData === 'object' ? singleParsedData : null,
        hblList: [],
    };
}

/**
 * MBL (or single document) parsed result → main form payload.
 * @returns {{payload: Object, optionsToAdd: Object}} optionsToAdd holds values to seed into the Autocomplete options.
 */
function buildMainFormPayload(data, formFieldKeys) {
    const parsedData = data?.parsedData ?? data;
    if (!parsedData || typeof parsedData !== 'object') return { payload: {}, optionsToAdd: {} };

    const payload = {};
    const optionsToAdd = {};

    Object.entries(PARSED_FIELD_MAP).forEach(([formKey, { parsedKey, kind }]) => {
        if (!formFieldKeys.has(formKey)) return;
        const parsedVal = parsedData[parsedKey] ?? parsedData[formKey];
        if (parsedVal === undefined || parsedVal === null) return;

        const resolved = resolveMappedValue(parsedVal, kind);
        payload[formKey] = resolved;
        optionsToAdd[formKey] = typeof resolved === 'string' ? [] : [resolved];
    });

    Object.keys(parsedData).forEach((key) => {
        if (payload[key] !== undefined) return;
        const raw = parsedData[key];
        if (raw === undefined || raw === null) return;
        if (key === 'mbl' && formFieldKeys.has('mawb')) {
            payload.mawb = raw;
            return;
        }
        if (!formFieldKeys.has(key)) return;
        payload[key] = raw;
    });

    return { payload, optionsToAdd };
}

/** True for the placeholder HBL tab added on dialog open that the user has not filled in yet. */
function isDefaultEmptyTab(row, { hblNumberKey, cntrListKey }) {
    if (!row) return false;
    const num = row[hblNumberKey];
    const hasNumber = num !== undefined && num !== null && String(num).trim() !== '';
    const hasValue = (x) =>
        x != null && x !== '' && (typeof x !== 'object' || x.id != null || (x.name != null && String(x.name).trim() !== ''));
    const subList = cntrListKey ? row[cntrListKey] || [] : [];
    return (
        !hasNumber &&
        !hasValue(row.il_trade_partner_id_shipper) &&
        !hasValue(row.il_trade_partner_id_consignee) &&
        subList.length === 0
    );
}

/** Starting id for new HBL tabs. If the only existing tab is an untouched placeholder, drop it and restart from 1. */
function resolveHblIdStart(currentHblList, hasIncomingHbl, tabKeys) {
    if (hasIncomingHbl && currentHblList.length === 1 && isDefaultEmptyTab(currentHblList[0], tabKeys)) {
        return { nextHblId: 1, tabIdToDelete: currentHblList[0].id };
    }
    const maxId = currentHblList.length > 0 ? Math.max(...currentHblList.map((h) => h.id), 0) : 0;
    return { nextHblId: maxId + 1, tabIdToDelete: null };
}

/**
 * Derive this shipment type's keys and flags from dialogConfig in one place,
 * so the same rule (hblNumberKey above all) does not drift across call sites.
 */
function deriveShipmentKeys(dialogConfig) {
    const isOcean = dialogConfig.type === 'ocean';
    return {
        isOcean,
        isAir: dialogConfig.type === 'air',
        isOex: dialogConfig.tableName === 'il_ocean_export',
        hblNumberKey: isOcean ? 'hbl' : 'hawb',
        hblListKey: dialogConfig.keyName?.[0],
        cntrListKey: dialogConfig.keyName?.[1],
    };
}

/** One HBL document → HBL tab payload (ocean = container rows, air = alias rows). */
function buildHblPayload(hblItem, hblId, keys) {
    const pd = hblItem?.parsedData || hblItem;
    if (!pd || typeof pd !== 'object') return null;

    const { isOcean, isAir, isOex, cntrListKey, hblNumberKey } = keys;

    const payload = {
        id: hblId,
        [hblNumberKey]: pd.hbl ?? pd.hawb ?? pd[hblNumberKey] ?? '',
        il_trade_partner_id_shipper: '',
        il_trade_partner_id_consignee: '',
        reference: pd.reference ?? '',
    };

    if (isOcean && cntrListKey) {
        const containers = Array.isArray(pd.containers) ? pd.containers : [];
        payload[cntrListKey] = containers.map((c, i) => ({
            id: hblId * 1000 + i + 1,
            cntr_number: c.number ?? c.cntr_number ?? '',
            volume: c.size ?? c.volume ?? '',
            ...(isOex && { seal_number: c.seal ?? '' }),
        }));
    }
    if (isAir && cntrListKey) {
        const aliases = Array.isArray(pd.aliases) ? pd.aliases : [];
        payload[cntrListKey] = aliases.map((a, i) => ({
            id: hblId * 1000 + i + 1,
            alias: a.alias ?? '',
            il_opus_vessel_id: a.flight ?? a.il_opus_vessel_id ?? '',
            quantity: a.volume != null ? Number(a.volume) : 0,
            il_opus_volume_unit_id: a.unit ?? a.il_opus_volume_unit_id ?? '',
            weight: a.weight != null ? Number(a.weight) : 0,
        }));
    }

    return payload;
}

/** Prepend parsed options to the existing Autocomplete options, skipping duplicates. */
function mergeAutocompleteOptions(prev, optionsToAdd) {
    const next = { ...prev };
    Object.keys(optionsToAdd).forEach((key) => {
        const existing = prev[key]?.options ?? [];
        const toAdd = (optionsToAdd[key] || []).filter(
            (opt) =>
                !existing.some(
                    (e) =>
                        (e.id != null && e.id === opt.id) ||
                        (e.name === opt?.name && (e.code === opt?.code || (e.code == null && opt?.code == null)))
                )
        );
        if (toAdd.length === 0) return;
        next[key] = { ...prev[key], options: [...toAdd, ...existing] };
    });
    return next;
}

export default function useDocumentApplyToForm(dialogConfig, setAutocompleteOptions) {
    const dispatch = useDispatch();
    const store = useStore();

    const formFieldKeys = useMemo(() => getFormFieldKeys(dialogConfig), [dialogConfig]);
    const keys = useMemo(() => deriveShipmentKeys(dialogConfig), [dialogConfig]);

    return useCallback(
        (applyPayload) => {
            const { mainSource, hblList } = splitApplyPayload(applyPayload);
            const { payload: mainPayload, optionsToAdd: mainOptionsToAdd } = mainSource
                ? buildMainFormPayload(mainSource, formFieldKeys)
                : { payload: {}, optionsToAdd: {} };

            const addHBLReducerName = dialogConfig.reducerActionsHBL?.[0];
            const deleteHBLReducerName = dialogConfig.reducerActionsHBL?.[2];
            const currentHblList = store.getState()[dialogConfig.formState]?.[keys.hblListKey] || [];

            const { nextHblId: startHblId, tabIdToDelete } = resolveHblIdStart(currentHblList, hblList.length > 0, keys);
            if (tabIdToDelete != null && deleteHBLReducerName) {
                dispatch(actionMap[deleteHBLReducerName](tabIdToDelete));
            }

            let nextHblId = startHblId;
            let appliedHblCount = 0;
            hblList.forEach((hblItem) => {
                const hblPayload = buildHblPayload(hblItem, nextHblId, keys);
                if (!hblPayload) return;
                dispatch(actionMap[addHBLReducerName](hblPayload));
                nextHblId += 1;
                appliedHblCount += 1;
            });

            const appliedMainCount = Object.keys(mainPayload).length;
            if (appliedMainCount > 0) {
                dispatch(actionMap[dialogConfig.setFormDataAction](mainPayload));
                setAutocompleteOptions((prev) => mergeAutocompleteOptions(prev, mainOptionsToAdd));
            }

            if (appliedMainCount + appliedHblCount > 0) {
                const subLabel = keys.isAir ? 'HAWB(s)' : 'HBL(s)';
                dispatch(
                    setSnackbar({
                        open: true,
                        message: `Applied ${appliedMainCount} main field(s) and ${appliedHblCount} ${subLabel} from document(s).`,
                        severity: 'success',
                    })
                );
            }
        },
        [dispatch, store, dialogConfig, formFieldKeys, keys, setAutocompleteOptions]
    );
}
